import { useMutation } from '@apollo/client';
import Loader from 'components/Loader';
import SmkAsyncSelectGraphql from 'components/SmkAsyncSelect/customSelectGraphql';
import TableError from 'components/TableError';
import { isEmpty, isObject, isString } from 'lodash';
import React, { useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { FOOD_ITEM_REF_SOURCES } from 'utils/constants';
import { FOOD_ITEM_UNITS, PREPARATION_STATE, RECIPE_CONDITIONS } from '../constants';
import { FOOD_ITEMS_SEARCH } from '../graphql/food_item.query';
import {
  ADD_RECIPE_INGREDIENTS,
  DELETE_RECIPE_INGREDIENTS,
  UPDATE_RECIPE_INGREDIENTS,
} from '../graphql/recipe.mutation';
import SubstituteCard from './SubstituteCard';
import CreatableSelect from 'react-select/creatable';

const IngredientsCard = ({ formik, recipeId, allIngredients, getAllIngredientsRequest }) => {
  toast.configure();

  const defaultIngredient = {
    isOptional: false,
    group: '',
    quantity: '',
    quantityUnit: '',
    preparationState: '',
    condition: '',
    substitutes: [],
  };

  const groupsList = [
    // If "default" is not present
    ...(allIngredients?.map((e) => e?.group)?.includes('default') ? [] : [{ label: 'Default', value: 'default' }]),

    // Add all unique groups
    ...Array.from(
      new Map(
        allIngredients
          ?.filter((e) => e?.group) // skip empty groups
          ?.map((e) => [e.group, { label: e.group, value: e.group }])
      ).values()
    ),
  ];

  const [ingredient, setIngredient] = useState(defaultIngredient);

  const refSourcesList = Object.keys(FOOD_ITEM_REF_SOURCES)?.map((e) => ({
    label: e?.toUpperCase(),
    value: FOOD_ITEM_REF_SOURCES[e],
  }));

  const [refSource, setRefSource] = useState('');

  const [showAddIngredient, setShowAddIngredient] = useState(false);
  const [selectedFoodItem, setSelectedFoodItem] = useState();
  const [editIngredientId, setEditIngredientId] = useState('');
  const [quantityUnitsList, setQuantityUnitsList] = useState(null);

  const handleChangeQuantityUnits = (type) => {
    // const selectedType = FOOD_ITEM_UNITS?.[type];

    // if (!isEmpty(selectedType)) {
    //   const data = selectedType?.map((e) => ({
    //     label: e?.toUpperCase(),
    //     value: e,
    //   }));

    //   setQuantityUnitsList(data);
    // } else {
    //   const allUnits = [...new Set(Object.values(FOOD_ITEM_UNITS)?.flat(2) ?? [])]?.flat(2)?.map((e) => ({
    //     label: e?.toUpperCase(),
    //     value: e,
    //   }));
    //   setQuantityUnitsList(allUnits);
    // }

    const allUnits = [...new Set(Object.values(FOOD_ITEM_UNITS)?.flat(2) ?? [])]?.flat(2)?.map((e) => ({
      label: e?.toUpperCase(),
      value: e,
    }));
    setQuantityUnitsList(allUnits);
  };

  const handleClearAllData = () => {
    setShowAddIngredient(false);
    setEditIngredientId('');
    setRefSource('');

    setSelectedFoodItem(null);
    handleChangeQuantityUnits(null);
    setIngredient(defaultIngredient);
  };

  const [addRecipeIngredientRequest, { loading: addRecipeIngredientLoading }] = useMutation(ADD_RECIPE_INGREDIENTS, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_recipe_ingredient_add)) {
        toast.success(`Success! Recipe Ingredient has been added.`);
        // window.location.reload();
        getAllIngredientsRequest({
          variables: {
            recipeId,
          },
        });
        handleClearAllData();
      }
    },
    onError: (err) => {
      const errorMessage =
        err?.graphQLErrors?.[0]?.extensions?.exception?.response?.message ||
        err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        '';

      const message = errorMessage?.toLowerCase()?.includes('duplicate')
        ? 'This record already exists and cannot be added again.'
        : 'Something went wrong!';

      toast.error(message);
    },
  });

  const [updateRecipeIngredientRequest, { loading: updateRecipeIngredientLoading }] = useMutation(
    UPDATE_RECIPE_INGREDIENTS,
    {
      onCompleted: (data) => {
        if (!isEmpty(data?.admin_nutrition_recipe_ingredient_update)) {
          toast.success(`Success! Recipe Ingredient has been updated.`);
          // window.location.reload();
          getAllIngredientsRequest({
            variables: {
              recipeId,
            },
          });
          handleClearAllData();
        }
      },
      onError: (err) => {
        const errorMessage =
          err?.graphQLErrors?.[0]?.extensions?.exception?.response?.message ||
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          '';

        const message = errorMessage?.toLowerCase()?.includes('duplicate')
          ? 'This record already exists and cannot be added again.'
          : 'Something went wrong!';
        toast.error(message);
      },
    }
  );

  const [deleteRecipeIngredientRequest, { loading: deleteRecipeIngredientLoading }] = useMutation(
    DELETE_RECIPE_INGREDIENTS,
    {
      onCompleted: (data) => {
        if (data?.admin_nutrition_recipe_ingredient_delete) {
          toast.success(`Success! Recipe Ingredient has been deleted.`);
          // window.location.reload();
          getAllIngredientsRequest({
            variables: {
              recipeId,
            },
          });
          handleClearAllData();
        }
      },
      onError: () => {
        toast.error('Something went wrong!');
      },
    }
  );

  // Add Ingredient in Listing
  const handleSubmit = () => {
    const payload = {
      recipeId,
      foodItemId: selectedFoodItem?.id,
      isOptional: ingredient?.isOptional,
      group: ingredient?.group,
      quantity: ingredient?.quantity,
      quantityUnit: ingredient?.quantityUnit,
      preparationState: ingredient?.preparationState,
      condition: ingredient?.condition,
      substitutes: ingredient?.substitutes.map((sub) => ({
        id: sub?.id,
        name: sub?.name,
      })),
    };

    if (editIngredientId) {
      updateRecipeIngredientRequest({
        variables: {
          recipeIngredientId: editIngredientId,
          recipeIngredient: payload,
        },
      });
    } else {
      addRecipeIngredientRequest({
        variables: {
          recipeIngredient: payload,
        },
      });
    }
  };

  // Delete Ingredient in Listing
  const handleDelete = (elem) => {
    deleteRecipeIngredientRequest({
      variables: {
        recipeIngredientId: elem?.id,
      },
    });
  };

  return (
    <div className="mb-5">
      <Loader isActive={addRecipeIngredientLoading || updateRecipeIngredientLoading || deleteRecipeIngredientLoading} />
      <Card>
        <CardBody>
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <div className="text-start">
              <h5 className="mb-2">{allIngredients.length} ingredients</h5>
              <h6 className="mb-0">Ingredients must be for {formik.values.servesCount || 0} servings</h6>
            </div>
            <a
              href="#nutrition_ingredients"
              className="btn btn-success text-white"
              onClick={() => {
                setShowAddIngredient(true);
                setSelectedFoodItem();
                setEditIngredientId('');
                setIngredient(defaultIngredient);
              }}>
              Add New
            </a>
          </div>

          {!isEmpty(allIngredients) && (
            <div className="table-responsive mt-3 pb-5" style={{ minHeight: 300 }}>
              <Table className="project-list-table   table-centered">
                <thead>
                  <tr className="table-warning">
                    <th>S.No.</th>
                    <th>Group</th>
                    <th>Ingredient</th>
                    <th>Ref Source</th>
                    <th>Quantity</th>
                    <th>Quantity Unit</th>
                    <th>Condition</th>
                    <th>Preparation State</th>
                    <th>Substitutes</th>
                    <th>isOptional</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!isEmpty(allIngredients) ? (
                    allIngredients?.map((elem, index) => {
                      return (
                        <tr>
                          <td>{index + 1}</td>
                          <td>{elem?.group}</td>
                          <td>{elem?.foodItem?.name}</td>
                          <td>{elem?.foodItem?.refSource?.toUpperCase()}</td>
                          <td>{elem?.quantity}</td>
                          <td>{elem?.quantityUnit?.toUpperCase()}</td>
                          <td>{elem?.condition}</td>
                          <td>{elem?.preparationState}</td>
                          <td>
                            {elem?.substitutes?.map((e) => (
                              <badge className="badge bg-dark me-3 h6 p-1">{e?.name}</badge>
                            ))}
                          </td>
                          <td>
                            <Badge className={elem?.isOptional ? 'bg-success' : 'bg-danger'}>
                              {elem?.isOptional ? 'Yes' : 'No'}
                            </Badge>
                          </td>
                          <td>
                            <UncontrolledDropdown>
                              <DropdownToggle href="#" className="card-drop" tag="i">
                                <i role="button" className="mdi mdi-dots-vertical font-size-18" />
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem
                                  href="#nutrition_ingredients"
                                  onClick={() => {
                                    setShowAddIngredient(true);
                                    setSelectedFoodItem(elem?.foodItem);
                                    setEditIngredientId(elem?.id);
                                    setIngredient(elem);
                                    setRefSource(elem?.foodItem?.refSource);
                                    handleChangeQuantityUnits(elem?.foodItem?.type?.slug);
                                  }}>
                                  <i className="fas fa-pencil-alt text-success m-1 pe-3" />
                                  Edit
                                </DropdownItem>
                                <DropdownItem onClick={() => handleDelete(elem)}>
                                  <i className={'fas fa-trash-alt text-danger m-1 pe-3'} />
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <TableError />
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {showAddIngredient && (
            <div className="mt-5" id="nutrition_ingredients">
              <div className="bg-light p-4 rounded-2">
                <div className="mb-0">
                  <div className="d-flex align-items-center justify-content-between">
                    <CardTitle className="mb-2">Food Item</CardTitle>
                    <div>
                      {refSourcesList?.map((option, i) => (
                        <div key={`refSource_${i}`} className="form-check mb-1 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`refSource_${option?.value}`}
                            name="refSource"
                            className="form-check-input"
                            checked={refSource?.toLowerCase() === option?.value?.toLowerCase()}
                            onChange={() => setRefSource(option?.value)}
                          />
                          <label className="form-check-label mb-0" htmlFor={`refSource_${option?.value}`}>
                            {option?.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <SmkAsyncSelectGraphql
                    // creatable={true}
                    isClearable={true}
                    acceptedKey="name"
                    acceptedValue="id"
                    placeholder="Type food item name..."
                    searchKeyName="name"
                    query={FOOD_ITEMS_SEARCH}
                    queryName="admin_nutrition_foodItem_search"
                    dtoName="searchDTO"
                    filters={{
                      autoComplete: true,
                      active: true,
                      page: 1,
                      size: 500,
                      sortBy: 'id',
                      sortOrder: 0,
                      language: formik.values.language,
                      ...(!isEmpty(refSource) ? { refSource } : {}),
                    }}
                    value={
                      !isEmpty(selectedFoodItem)
                        ? {
                            label: selectedFoodItem?.name,
                            value: selectedFoodItem?.slug,
                          }
                        : null
                    }
                    onChange={(data) => {
                      setSelectedFoodItem(data);
                      handleChangeQuantityUnits(data?.type?.slug);

                      if (isEmpty(data)) {
                        setIngredient(defaultIngredient);
                      }
                    }}
                  />

                  <p className="mt-3 mb-0">
                    If you didn't find your food item in the list, please click this link:{' '}
                    <a href="/nutrition/food-items/add" target="_blank">
                      Add Food Item
                    </a>
                  </p>
                </div>
              </div>

              {!isEmpty(selectedFoodItem?.name) && (
                <Form className="mb-3 border p-4 rounded-2 bg-light pt-0">
                  <Row>
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="group">Group</Label>
                        <CreatableSelect
                          options={groupsList}
                          placeholder="Type group name.."
                          value={
                            groupsList?.find((e) => e?.value?.toLowerCase() === ingredient.group?.toLowerCase()) ||
                            !isEmpty(ingredient?.group)
                              ? {
                                  label: ingredient?.group,
                                  value: ingredient?.group,
                                }
                              : null
                          }
                          onChange={(res) => {
                            setIngredient({
                              ...ingredient,
                              group: res?.value,
                            });
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="quantity">Quantity</Label>
                        <Input
                          type="number"
                          name="quantity"
                          placeholder="Enter quantity"
                          value={ingredient.quantity}
                          onChange={(e) =>
                            setIngredient({
                              ...ingredient,
                              quantity: e.target.value,
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="quantityUnit">Quantity Unit</Label>
                        <Select
                          options={quantityUnitsList}
                          placeholder="Select quantity unit..."
                          value={quantityUnitsList?.find(
                            (e) => e?.value?.toLowerCase() === ingredient?.quantityUnit?.toLowerCase()
                          )}
                          onChange={(e) =>
                            setIngredient({
                              ...ingredient,
                              quantityUnit: e.value,
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="preparationState">Preparation State</Label>
                        <CreatableSelect
                          options={PREPARATION_STATE}
                          placeholder="Type preparation name.."
                          value={
                            PREPARATION_STATE?.find(
                              (e) => e?.value?.toLowerCase() === ingredient.preparationState?.toLowerCase()
                            ) || !isEmpty(ingredient?.preparationState)
                              ? { label: ingredient?.preparationState, value: ingredient?.preparationState }
                              : null
                          }
                          onChange={(res) => {
                            setIngredient({
                              ...ingredient,
                              preparationState: res?.value,
                            });
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="condition">Condition</Label>
                        <CreatableSelect
                          options={RECIPE_CONDITIONS}
                          placeholder="Type condition name.."
                          value={
                            RECIPE_CONDITIONS?.find(
                              (e) => e?.value?.toLowerCase() === ingredient.condition?.toLowerCase()
                            ) || !isEmpty(ingredient?.condition)
                              ? { label: ingredient?.condition, value: ingredient?.condition }
                              : null
                          }
                          onChange={(res) => {
                            setIngredient({
                              ...ingredient,
                              condition: res?.value,
                            });
                          }}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup check className={`mb-4 `}>
                        <Label check>
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            name="isOptional"
                            checked={ingredient.isOptional}
                            onChange={(e) =>
                              setIngredient({
                                ...ingredient,
                                isOptional: e.target.checked,
                              })
                            }
                          />
                          Is Optional
                        </Label>
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <div className="bg-white rounded-2 p-3">
                        <SubstituteCard
                          className={'bg-white p-0'}
                          onChange={(res) =>
                            setIngredient({
                              ...ingredient,
                              substitutes: res,
                            })
                          }
                          initialValues={ingredient.substitutes}
                          filters={{
                            language: formik.values.language,
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Form>
              )}

              <div className="d-flex gap-4 mt-4">
                <Button type="button" color="primary" disabled={isEmpty(selectedFoodItem)} onClick={handleSubmit}>
                  {editIngredientId ? 'Save' : 'Add'} Ingredient
                </Button>

                <Button type="button" color="light" onClick={handleClearAllData}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default IngredientsCard;
